import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Award,
  Calendar,
  User,
  ChevronRight
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import BeltBadge, { BeltIndicator, getBeltName } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// Promotion Form Modal
// ============================================
const PromotionForm = ({ student, promotion, onSave, onCancel }) => {
  const { getBeltOptions } = useBelts();
  const beltOptions = getBeltOptions();

  const [formData, setFormData] = useState({
    from_belt: promotion?.from_belt || student?.current_belt || 'white',
    to_belt: promotion?.to_belt || 'white',
    from_stripes: promotion?.from_stripes || student?.current_stripes || 0,
    to_stripes: promotion?.to_stripes || 0,
    promotion_date: promotion?.promotion_date || new Date().toISOString().split('T')[0],
    promoted_by: promotion?.promoted_by || '',
    certificate_url: promotion?.certificate_url || '',
    notes: promotion?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  // Auto-fill from current belt when creating new promotion
  useEffect(() => {
    if (!promotion && student) {
      setFormData(prev => ({
        ...prev,
        from_belt: student.current_belt,
        from_stripes: student.current_stripes,
      }));
    }
  }, [student, promotion]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = promotion?.id
        ? `${API_BASE}/api/admin/promotions/${promotion.id}`
        : `${API_BASE}/api/admin/students/${student.id}/promotions`;

      const response = await fetch(url, {
        method: promotion?.id ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          from_stripes: parseInt(formData.from_stripes) || 0,
          to_stripes: parseInt(formData.to_stripes) || 0,
        })
      });

      if (response.ok) {
        const savedPromotion = await response.json();
        onSave(savedPromotion);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save promotion');
      }
    } catch (error) {
      console.error('Failed to save promotion:', error);
      alert('Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-2 border-amber-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Award className="h-5 w-5 text-amber-600" />
          {promotion?.id ? 'Edit Promotion' : 'Record New Promotion'}
        </CardTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Recording promotion for: <strong>{student.name}</strong>
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* From Belt */}
            <div>
              <Label htmlFor="from_belt">From Belt</Label>
              <select
                id="from_belt"
                value={formData.from_belt}
                onChange={(e) => setFormData({ ...formData, from_belt: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {beltOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* From Stripes */}
            <div>
              <Label htmlFor="from_stripes">From Stripes</Label>
              <Input
                id="from_stripes"
                type="number"
                min="0"
                max="4"
                value={formData.from_stripes}
                onChange={(e) => setFormData({ ...formData, from_stripes: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* To Belt */}
            <div>
              <Label htmlFor="to_belt">To Belt</Label>
              <select
                id="to_belt"
                value={formData.to_belt}
                onChange={(e) => setFormData({ ...formData, to_belt: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {beltOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* To Stripes */}
            <div>
              <Label htmlFor="to_stripes">To Stripes</Label>
              <Input
                id="to_stripes"
                type="number"
                min="0"
                max="4"
                value={formData.to_stripes}
                onChange={(e) => setFormData({ ...formData, to_stripes: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Promotion Preview */}
            <div className="md:col-span-2 flex items-center justify-center gap-4 py-4 px-3 bg-amber-50 dark:bg-amber-900/30 rounded">
              <div className="text-center">
                <BeltBadge
                  belt={formData.from_belt}
                  stripes={parseInt(formData.from_stripes) || 0}
                  size="md"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From</p>
              </div>
              <ChevronRight className="h-6 w-6 text-amber-600" />
              <div className="text-center">
                <BeltBadge
                  belt={formData.to_belt}
                  stripes={parseInt(formData.to_stripes) || 0}
                  size="md"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">To</p>
              </div>
            </div>

            {/* Promotion Date */}
            <div>
              <Label htmlFor="promotion_date">Promotion Date</Label>
              <Input
                id="promotion_date"
                type="date"
                value={formData.promotion_date}
                onChange={(e) => setFormData({ ...formData, promotion_date: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Promoted By */}
            <div>
              <Label htmlFor="promoted_by">Promoted By (Instructor)</Label>
              <Input
                id="promoted_by"
                value={formData.promoted_by}
                onChange={(e) => setFormData({ ...formData, promoted_by: e.target.value })}
                placeholder="Instructor name"
                className="mt-1"
              />
            </div>

            {/* Certificate URL */}
            <div className="md:col-span-2">
              <Label htmlFor="certificate_url">Certificate URL (optional)</Label>
              <Input
                id="certificate_url"
                value={formData.certificate_url}
                onChange={(e) => setFormData({ ...formData, certificate_url: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Link to uploaded certificate image</p>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any notes about this promotion..."
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500"
                rows={2}
              />
            </div>
          </div>

          <div className="flex space-x-2 mt-4">
            <Button type="submit" disabled={saving} variant="adminGold">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : (promotion?.id ? 'Update' : 'Record Promotion')}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================
// Promotion List Item
// ============================================
const PromotionListItem = ({ promotion, onEdit, onDelete }) => {
  return (
    <div className="flex items-center gap-4 p-3 bg-background rounded border hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-3 flex-1">
        <BeltIndicator belt={promotion.from_belt} stripes={promotion.from_stripes} />
        <ChevronRight className="h-4 w-4 text-gray-400" />
        <BeltIndicator belt={promotion.to_belt} stripes={promotion.to_stripes} />
        <div className="flex-1 ml-2">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-3 w-3 text-gray-400" />
            <span>{new Date(promotion.promotion_date).toLocaleDateString()}</span>
            {promotion.promoted_by && (
              <>
                <span className="text-gray-300 dark:text-gray-600 dark:text-gray-400">|</span>
                <User className="h-3 w-3 text-gray-400" />
                <span className="text-gray-600 dark:text-gray-400">{promotion.promoted_by}</span>
              </>
            )}
          </div>
          {promotion.notes && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate max-w-md">{promotion.notes}</p>
          )}
        </div>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => onEdit(promotion)}>
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(promotion)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// Main Belt Promotion Editor Component
// ============================================
const BeltPromotionEditor = ({ student, onStudentUpdated }) => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  useEffect(() => {
    if (student?.id) {
      fetchPromotions();
    }
  }, [student?.id]);

  const fetchPromotions = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/students/${student.id}/promotions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPromotions(data);
      }
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (savedPromotion) => {
    await fetchPromotions();
    setShowForm(false);
    setEditingPromotion(null);
    // Notify parent that student was updated (belt may have changed)
    if (onStudentUpdated) {
      onStudentUpdated();
    }
  };

  const handleDelete = async (promotion) => {
    if (!window.confirm('Are you sure you want to delete this promotion record?')) return;

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/promotions/${promotion.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await fetchPromotions();
      }
    } catch (error) {
      console.error('Failed to delete promotion:', error);
    }
  };

  const startEdit = (promotion) => {
    setEditingPromotion(promotion);
    setShowForm(true);
  };

  const startAdd = () => {
    setEditingPromotion(null);
    setShowForm(true);
  };

  if (!student) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        Select a student to manage their belt promotions.
      </div>
    );
  }

  if (loading) {
    return <div className="text-center py-4">Loading promotions...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Current Belt Display */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/30 dark:to-amber-800/30 rounded-lg">
        <div>
          <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">Current Belt</p>
          <p className="text-lg font-semibold text-amber-900 dark:text-amber-100">
            {getBeltName(student.current_belt, student.current_stripes)}
          </p>
        </div>
        <BeltBadge
          belt={student.current_belt}
          stripes={student.current_stripes}
          size="lg"
        />
      </div>

      {/* Add Button */}
      {!showForm && (
        <Button onClick={startAdd} variant="adminGold">
          <Plus className="h-4 w-4 mr-2" />
          Record Promotion
        </Button>
      )}

      {/* Promotion Form */}
      {showForm && (
        <PromotionForm
          student={student}
          promotion={editingPromotion}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingPromotion(null); }}
        />
      )}

      {/* Promotion History */}
      {!showForm && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <Award className="h-4 w-4" />
            Promotion History ({promotions.length})
          </h4>
          {promotions.length === 0 ? (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded">
              No promotion history yet.
            </div>
          ) : (
            <div className="space-y-2">
              {promotions.map(promotion => (
                <PromotionListItem
                  key={promotion.id}
                  promotion={promotion}
                  onEdit={startEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BeltPromotionEditor;
